/**
 * useContactTags - Hook centralizado para Tags de Contato (contact_tags)
 *
 * Espelha o padrão já consolidado em useDealTags.ts:
 *   - 100% server-side (nenhum acesso direto à tabela contact_tags)
 *   - Leitura reaproveita a RPC existente `get_tenant_contact_tags`
 *   - Escrita usa as RPCs seguras `create_contact_tag`, `update_contact_tag`
 *     e `delete_contact_tag` (padrão tenant v2.1 com permissão granular)
 *   - Cache centralizado na query key ['contact-tags', tenantId]
 *
 * RELEASE: 2026-08-29 v1.0.0 - Versão inicial (elimina duplicação de
 *          consultas inline a contact_tags espalhadas pelo frontend).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { createLogger } from '@/lib/logger';

// Logger condicional por ambiente (padrão do projeto)
const logger = createLogger('useContactTags');

/** Tag de contato retornada pela RPC de leitura */
export interface ContactTag {
  id: string;
  name: string;
  color: string;
}

/** Chave de cache compartilhada por todos os consumidores */
export const contactTagsQueryKey = (tenantId?: string) => ['contact-tags', tenantId] as const;

// ============================================================================
// LEITURA - reaproveita RPC existente get_tenant_contact_tags
// ============================================================================

export function useContactTags(tenantId: string | undefined, enabled = true) {
  return useQuery({
    queryKey: contactTagsQueryKey(tenantId),
    queryFn: async () => {
      // A RPC resolve o tenant internamente; o hint é apenas uma sugestão validada
      const { data, error } = await supabase.rpc('get_tenant_contact_tags', {
        p_tenant_hint: tenantId ?? undefined,
      });

      if (error) {
        logger.error('Erro ao buscar tags de contato', error);
        throw error;
      }

      return ((data || []) as any[]).map((t) => ({
        id: t.id,
        name: t.name,
        color: t.color || '#6366f1',
      })) as ContactTag[];
    },
    enabled: !!tenantId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutos
  });
}

// ============================================================================
// ESCRITA - RPCs seguras (criar / editar / excluir)
// ============================================================================

/** Invalida o cache de tags de todos os consumidores */
function useInvalidateContactTags() {
  const queryClient = useQueryClient();
  return () => {
    queryClient.invalidateQueries({ queryKey: ['contact-tags'] });
  };
}

export function useCreateContactTag() {
  const invalidate = useInvalidateContactTags();

  return useMutation({
    mutationFn: async (params: { name: string; color: string; tenantId?: string }) => {
      const { data, error } = await supabase.rpc('create_contact_tag', {
        p_name: params.name,
        p_color: params.color,
        p_tenant_hint: params.tenantId ?? undefined,
      });

      if (error) {
        logger.error('Erro ao criar tag de contato', error);
        throw error;
      }

      return data as string;
    },
    onSuccess: invalidate,
  });
}

export function useUpdateContactTag() {
  const invalidate = useInvalidateContactTags();

  return useMutation({
    mutationFn: async (params: {
      tagId: string;
      name: string;
      color: string;
      tenantId?: string;
    }) => {
      const { error } = await supabase.rpc('update_contact_tag', {
        p_tag_id: params.tagId,
        p_name: params.name,
        p_color: params.color,
        p_tenant_hint: params.tenantId ?? undefined,
      });

      if (error) {
        logger.error('Erro ao atualizar tag de contato', error);
        throw error;
      }
    },
    onSuccess: invalidate,
  });
}

export function useDeleteContactTag() {
  const invalidate = useInvalidateContactTags();

  return useMutation({
    mutationFn: async (params: { tagId: string; tenantId?: string }) => {
      const { error } = await supabase.rpc('delete_contact_tag', {
        p_tag_id: params.tagId,
        p_tenant_hint: params.tenantId ?? undefined,
      });

      if (error) {
        logger.error('Erro ao excluir tag de contato', error);
        throw error;
      }
    },
    onSuccess: invalidate,
  });
}
